import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "../mock";
import { useReservation } from "./ReservationModal";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Menu", to: "/menu" },
  { label: "Reservations", to: "/reservations" },
  { label: "Private Events", to: "/private-events" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { open: openReservation } = useReservation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-nala-black/95 backdrop-blur-md border-b border-nala-gold/15 py-2"
          : "bg-gradient-to-b from-black/70 to-transparent py-4"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={BRAND.logos.gold}
            alt="Nala Kitchen & Cocktails"
            className={`w-auto transition-all duration-500 ${scrolled ? "h-11" : "h-14"}`}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `relative text-[0.78rem] uppercase tracking-[0.2em] font-medium transition-colors duration-300 py-1 ${
                  isActive ? "text-nala-gold" : "text-nala-cream hover:text-nala-gold"
                } after:content-[''] after:absolute after:left-0 after:-bottom-0.5 after:h-px after:bg-nala-gold after:transition-all after:duration-300 ${
                  isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a href={BRAND.phoneHref} className="hidden xl:flex items-center gap-2 text-nala-cream-muted hover:text-nala-gold transition-colors text-sm">
            <Phone size={15} />
            <span className="tracking-wide-lux">{BRAND.phone}</span>
          </a>
          <Link to="/private-events" className="btn-outline-gold !py-3 !px-5 !text-[0.7rem]">
            <span>Private Events</span>
          </Link>
          <button onClick={openReservation} className="btn-gold !py-3 !px-6">
            <span>Reserve</span>
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden text-nala-gold p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 bg-nala-black/98 backdrop-blur-md ${
          open ? "max-h-[520px] border-t border-nala-gold/15" : "max-h-0"
        }`}
      >
        <nav className="flex flex-col px-6 py-6 gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `py-3 text-sm uppercase tracking-[0.2em] border-b border-white/5 ${
                  isActive ? "text-nala-gold" : "text-nala-cream"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <button onClick={() => { setOpen(false); openReservation(); }} className="btn-gold mt-5 w-full">
            <span>Reserve a Table</span>
          </button>
        </nav>
      </div>
    </header>
  );
}
